// List view — the week as compact rows grouped by day: time, type dot, title,
// linked entity and status (task checkbox, overdue pill).

import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from '@govard/i18n-core/react';
import { Clock } from 'lucide-react';
import { useMemo, type ReactElement } from 'react';

import { useTheme } from '../../../theme/ThemeContext.js';
import {
  EVENT_ACCENT_COLOR,
  formatDayShort,
  groupEventsByDay,
  weekDays,
  type PlannerEvent,
  type PlannerViewHandlers,
} from '../lib/plannerEvents.js';
import { PlannerTaskCheckbox } from './PlannerEventCard.js';

interface ListViewProps extends PlannerViewHandlers {
  events: PlannerEvent[];
  weekStart: Dayjs;
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function ListRow({
  event,
  onOpen,
  onToggleTask,
}: PlannerViewHandlers & { event: PlannerEvent }): ReactElement {
  const { t } = useTranslation();
  const { actualTheme } = useTheme();
  const mode = actualTheme === 'dark' ? 'dark' : 'light';
  const accent = EVENT_ACCENT_COLOR[event.eventType][mode];
  const isDone = event.done;

  return (
    <div
      onClick={() => onOpen(event)}
      className="grid cursor-pointer grid-cols-[48px_14px_minmax(0,1fr)_auto] items-center gap-2.5 border-b px-2.5 py-1.5 transition-colors duration-[110ms] hover:bg-[var(--color-surface-hover,var(--g-fill))] md:grid-cols-[56px_18px_minmax(0,1fr)_minmax(150px,280px)_110px]"
      style={{ borderColor: 'var(--ant-color-border-secondary)' }}
    >
      <span
        className="font-mono text-[11px] font-semibold tabular-nums"
        style={{ color: 'var(--ant-color-text-tertiary)' }}
      >
        {event.timeLabel ?? '—'}
      </span>
      <span
        className="h-2 w-2 rounded-full"
        style={
          event.eventType === 'deadline'
            ? { backgroundColor: 'transparent', boxShadow: `inset 0 0 0 2px ${accent}` }
            : { backgroundColor: accent }
        }
      />
      <span
        className={`truncate text-[12.5px] font-medium ${isDone ? 'line-through' : ''}`}
        style={{ color: isDone ? 'var(--ant-color-text-tertiary)' : 'var(--ant-color-text)' }}
      >
        {event.title}
      </span>
      <span
        className="hidden truncate text-[11.5px] md:block"
        style={{ color: 'var(--ant-color-text-secondary)' }}
      >
        {event.entityLabel ?? ''}
      </span>
      <span className="flex items-center justify-end gap-1.5">
        {event.eventType === 'task' ? (
          <PlannerTaskCheckbox event={event} onToggleTask={onToggleTask} />
        ) : null}
        {event.overdue && !isDone ? (
          <span
            className="inline-flex items-center gap-1 rounded-full px-1.5 py-px text-[9px] font-bold uppercase tracking-wide"
            style={{
              // Semantic status tokens: --ant-* inside antd's cssVar scope
              // resolve to muted dark values that fail AA (known lesson).
              backgroundColor: 'var(--color-error, #b91c1c)',
              color: 'var(--crm-on-danger, #ffffff)',
            }}
          >
            <Clock size={9} strokeWidth={2.5} />
            {t('crm.calendar.overdue')}
          </span>
        ) : null}
      </span>
    </div>
  );
}

export function PlannerListView({
  events,
  weekStart,
  onOpen,
  onToggleTask,
}: ListViewProps): ReactElement {
  const { t, tc, locale } = useTranslation();
  const grouped = useMemo(() => groupEventsByDay(events), [events]);
  const todayKey = dayjs().format('YYYY-MM-DD');
  const days = weekDays(weekStart);

  return (
    <div>
      {days.map((day) => {
        const dateKey = day.format('YYYY-MM-DD');
        const group = grouped.get(dateKey) ?? { allDay: [], timed: [] };
        const rows = [...group.allDay, ...group.timed];
        const isToday = dateKey === todayKey;

        return (
          <div key={dateKey} className="mb-4">
            <div
              className="flex items-baseline gap-2.5 rounded-t-lg border-b px-2.5 py-1.5"
              style={{
                borderColor: 'var(--ant-color-border-secondary)',
                backgroundColor: isToday ? 'var(--ant-color-primary-bg)' : undefined,
              }}
            >
              <span
                className="text-[13px] font-bold"
                style={{
                  color: isToday ? 'var(--ant-color-primary-text)' : 'var(--ant-color-text)',
                }}
              >
                {capitalize(formatDayShort(day, locale))}
              </span>
              <span className="text-[11px]" style={{ color: 'var(--ant-color-text-tertiary)' }}>
                {rows.length > 0
                  ? tc('crm.calendar.list.count', rows.length)
                  : t('crm.calendar.list.empty')}
              </span>
            </div>
            {rows.length === 0 ? (
              <div
                className="border-b px-2.5 py-1.5 text-[11.5px]"
                style={{
                  borderColor: 'var(--ant-color-border-secondary)',
                  color: 'var(--ant-color-text-tertiary)',
                }}
              >
                {t('crm.calendar.list.no_events')}
              </div>
            ) : (
              rows.map((event) => (
                <ListRow key={event.id} event={event} onOpen={onOpen} onToggleTask={onToggleTask} />
              ))
            )}
          </div>
        );
      })}
    </div>
  );
}
